import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  FolderTree,
  Layers,
  Inbox,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Tier-2 (business cohort) content piece targeting "how to clean up a messy
 * shared team drive" and variants ("organize a messy shared drive",
 * "clean up a shared network drive nobody can find anything in").
 *
 * Shape-B: the headline is the cleanup project, not the product. Sortio is
 * positioned as the bulk re-sort engine inside a two-phase plan: a one-time
 * backlog pass (read each file, propose name + destination, preview, apply)
 * plus a standing watch-folder rule on intake so the drive does not drift
 * back. AEO-first: H1 is the query verbatim, first H2 repeats it and answers
 * in the first two to three sentences with Sortio named once.
 */

const PUBLISHED = '2026-06-05';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/clean-up-messy-shared-drive';

const FAQ = [
  {
    question: 'How do you clean up a messy shared team drive?',
    answer:
      'Treat it as two phases, not one heroic weekend. Phase one is a one-time backlog pass: agree on a single folder structure and naming template, then re-sort and rename the existing files to it. Phase two is a standing rule on the intake folder so every new file is named and routed on arrival and the drive does not drift back. The backlog is the part teams avoid for years because doing it by hand is a quarter of work; reading each file with an LLM and proposing a name and destination is what makes it tractable. Sortio runs that bulk pass in preview so you approve the moves before anything changes, then keeps the drive clean with a watch folder on intake.',
  },
  {
    question: 'Should we agree on a folder structure before we start cleaning?',
    answer:
      'Yes, and it is the single most important step. If you start dragging files before the structure and naming template are decided, you just create a second mess on top of the first. Pick one folder tree (by client or project, then by year or document type) and one filename template (date, entity, type) and write them down before a single file moves. Everything in the cleanup then has a correct home to go to, and the standing rule has a target to enforce. The structure decision is covered in depth in the client-project folder structure piece.',
  },
  {
    question: 'What do we do with the giant "Miscellaneous" or "To File" pile?',
    answer:
      'The miscellaneous pile is the whole problem in miniature: a folder of files nobody could classify, so they got dumped instead of filed. The fix is to read what is inside each one. Point Sortio at the miscellaneous folder, have it read each file and propose a destination in the new structure, and review the preview. Most of the pile resolves cleanly once content is read instead of guessed from a useless filename like final_v2.pdf or scan_0421.pdf. The handful that genuinely do not fit can be left in a clearly labelled holding folder, which is very different from a four-hundred-file black hole.',
  },
  {
    question: 'How do you handle duplicate and near-duplicate files?',
    answer:
      'Duplicates are normal on a shared drive: the same contract saved by three people, final.pdf next to final_v2.pdf next to final_FINAL.pdf. In a Sortio preview run you see the proposed name and destination for every file before anything moves, so exact-name collisions surface in one view and you decide which copy is canonical rather than discovering the conflict mid-drag. Run the backlog in preview, resolve the obvious duplicates by keeping the most recent or most complete version, and let the consistent naming template stop new duplicates from accumulating, because identical content now produces an identical, searchable name.',
  },
  {
    question: 'Can we clean up the drive without the risk of losing files?',
    answer:
      'The backlog pass is non-destructive by design. Sortio shows a full preview of every proposed rename and move, you approve it, and only then does anything change. Sortio keeps backup archives of your most recent sorts, so a wrong call can be undone from inside the app. For sensitive drives you can run the whole sort with local-only inference through Ollama, so file content never leaves the machine. The safe way to do a big cleanup is exactly this: preview, approve a sample, then apply to the rest, instead of a manual drag-and-drop marathon where a slip is permanent.',
  },
  {
    question: 'How do we keep the shared drive clean after the cleanup?',
    answer:
      'Put a standing rule on the intake folder. On Sortio Pro and Teams you point a watch folder at wherever new files land and the same template that cleaned the backlog now applies on every arrival, with no one opening the app. Run the watch in preview mode for the first week so it queues proposed moves and notifies you instead of applying them, confirm it is routing correctly, then switch to apply. From then on the drive stays clean by default because enforcement lives at the point of filing, not in a wiki page people forget. The naming side of this is covered in the enforce-a-naming-convention piece.',
  },
  {
    question: 'Does cleaning up a shared drive reduce the time staff spend filing?',
    answer:
      'Substantially, in both directions. The one-time backlog pass recovers the hours that would otherwise go into manually renaming and refiling thousands of legacy files, and the standing rule removes the per-file filing labor going forward, since new files are named and routed automatically. Staff drop documents into an intake folder and the structure is applied for them instead of each person deciding a name and dragging the file. The dedicated piece on reducing time spent filing and renaming documents breaks down where those hours actually go.',
  },
];

const CleanUpMessySharedDriveBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Clean up a messy shared drive', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Clean up a messy shared drive',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Clean Up a Messy Shared Team Drive',
    description:
      'Cleaning a messy shared drive is a two-phase project: a one-time backlog pass to re-sort and rename everything to one structure, then a standing rule on intake so it never drifts back. Target folder tree, a working cleanup prompt, and the manual vs rule-based vs AI comparison.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    author: {
      '@type': 'Organization',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Clean Up a Messy Shared Team Drive (Backlog + Standing Rule)"
        description="Cleaning a messy shared drive is a two-phase project: a one-time backlog pass to re-sort and rename everything, then a standing rule on intake so it never drifts back. Target folder tree, a copyable cleanup prompt, and the manual vs rule-based vs AI comparison."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="clean up messy shared drive, organize a messy shared drive, clean up shared network drive, shared drive cleanup, organize team drive, declutter shared folder, file the miscellaneous folder, keep shared drive organized"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Clean Up a Messy Shared Team Drive
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span>By the Sortio team</span>
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>10 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            Every growing team ends up with the same drive. Twelve thousand files, a folder
            called "Old", a folder called "Old 2", a "Miscellaneous" pile with four hundred
            things in it, and three copies of the same contract named{' '}
            <code>final.pdf</code>, <code>final_v2.pdf</code>, and{' '}
            <code>final_FINAL.pdf</code>. Nobody can find anything, so people re-create files
            that already exist, and the mess compounds.
          </p>

          <p>
            The reason cleanups stall is that teams treat it as one giant manual chore and
            never start. It is actually two distinct jobs: a one-time pass to fix the backlog,
            and a standing rule so it does not drift back. This post is the practical plan for
            both, including the folder tree to aim at, what to do with the miscellaneous pile
            and the duplicates, and how to do the heavy backlog pass without a manual task force.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <FolderTree className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Clean up a shared drive in two phases. First, agree on one folder structure
                  and one naming template, then run a single backlog pass that re-sorts and
                  renames the existing files to it. Second, put a standing rule on the intake
                  folder so new files are named and routed on arrival and the drive never drifts
                  back. Sortio is the bulk re-sort engine for the backlog: it reads each file,
                  proposes a name and destination, shows you a full preview, and applies only
                  after you approve, then runs as a watch folder to keep the drive clean.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How do you clean up a messy shared team drive?
          </h2>

          <p>
            You clean it up in two phases: a one-time backlog pass, then a standing rule that
            keeps it clean. The mistake that kills cleanups is treating the whole thing as a
            single manual marathon, so it never gets scheduled. Split it: fix what is there
            once, then enforce the structure at the point of filing so it stays fixed. Sortio
            handles the backlog by reading each file and proposing a name and destination in
            bulk, which is what turns a quarter of manual filing into a preview-and-approve
            session. The full sequence:
          </p>

          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Agree on the structure and naming template first.</strong> Decide the
              folder tree and the filename format before a single file moves. Without this you
              just create a second mess on top of the first.
            </li>
            <li>
              <strong>Run the backlog pass in preview.</strong> Point the tool at the drive,
              have it read each file and propose a name and folder, and review the proposed
              moves before anything changes.
            </li>
            <li>
              <strong>Resolve the miscellaneous pile and duplicates.</strong> The preview is
              where you see collisions and stray files in one place and decide the canonical
              copy, instead of discovering conflicts mid-drag.
            </li>
            <li>
              <strong>Apply, with backups.</strong> Approve the run and let it execute. Sortio
              keeps backup archives of your most recent sorts, so a wrong call can be undone
              from inside the app.
            </li>
            <li>
              <strong>Put a standing rule on intake.</strong> Watch the folder where new files
              land so they are named and routed on arrival, and the drive stays clean by default.
            </li>
          </ol>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 1: agree on one structure and naming template
          </h2>

          <p>
            This is the step teams skip, and skipping it is why the second cleanup looks like
            the first. Before anything moves, pick one folder tree and one filename template
            that every file type can follow. A structure organized by client or project, then
            by year or document type, covers most teams. Here is a concrete target to aim at:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Shared Drive/
  Clients/
    Acme-Corp/
      2026/
        Contracts/
        Invoices/
        Correspondence/
        Reports/
      2025/
    Northgate-Apartments/
      2026/
  Internal/
    HR/
    Finance/
    Templates/
  _Inbox/        <- new files land here, rule fires on arrival
  _Holding/      <- only for files that genuinely cannot be classified`}
          </pre>

          <p>
            Then settle the filename template so every folder is consistent. Lead with an ISO
            date so files sort chronologically in any file browser, then the entity, then a
            short document type, then an optional disambiguator. Hyphens inside a field,
            underscores between fields, so each field stays machine-parseable:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`{YYYY-MM-DD}_{Entity}_{DocType}_{Ref}.ext

2026-04-12_Acme-Corp_Invoice_4471.pdf
2026-04-15_Acme-Corp_Contract_MSA.pdf
2026-04-22_Northgate-Apartments_Lease_Unit-3B.pdf`}
          </pre>

          <p>
            This is not just an in-house preference.{' '}
            <a
              href="https://guides.library.stanford.edu/data-best-practices"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sortio-primary hover:underline"
            >
              Stanford University Libraries
            </a>{' '}
            advises that you "be consistent and descriptive in naming and organizing files so
            that it is obvious where to find specific data and what the files contain."
            Consistency is the property that makes a drive findable, and it is exactly what a
            human task force cannot hold by hand across twelve thousand files. For a deeper take
            on the tree itself, see the{' '}
            <Link
              to="/blog/folder-structure-client-projects"
              className="text-sortio-primary hover:underline"
            >
              folder structure for client and project work
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 2: the backlog pass, in preview
          </h2>

          <p>
            This is where a manual task force dies. Reading, renaming, and refiling thousands
            of legacy files by hand is weeks of work that nobody volunteers for, so the drive
            stays broken. The alternative is to let a tool read each file and propose the move,
            and to do the whole thing in preview so you stay in control.
          </p>

          <p>
            Point Sortio at the drive (or one top-level folder at a time, which is less
            intimidating), and give it a cleanup prompt that maps content to your structure.
            Drop this into the prompt box and run Preview before applying:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each file and file it into our shared-drive structure.

Folder layout: Clients/{Entity}/{Year}/{DocType}/
- Entity is the client, company, project, or property the
  file is about. Use hyphens, no spaces (Acme-Corp,
  Northgate-Apartments).
- Year comes from the document date; if there is none, use
  the file's own date.
- DocType is one of: Contracts, Invoices, Correspondence,
  Reports, Statements, Templates. Pick the closest.

Rename each file to:
{YYYY-MM-DD}_{Entity}_{DocType-singular}_{Ref}.ext
- Ref is an invoice number, matter name, or unit if present,
  else omit it.

Files that are clearly internal (HR, finance, templates) go
under Internal/ instead of Clients/.

If you cannot confidently identify the entity, route the file
to _Holding/ and leave its name unchanged. Do not guess.`}
          </pre>

          <p>
            Click Preview. Sortio shows the proposed name and destination for every file, plus
            the fields it read out of the content, so you validate the plan on a real sample
            before committing. If it mislabels a file in preview, untick that one so it stays
            where it is, or refine the prompt and run Preview again. Nothing moves until you
            apply. The
            "route to _Holding, do not guess" instruction is deliberate: it is better to leave
            a hard file unmoved than to scatter wrong guesses across the new structure.
          </p>

          <p>
            Apply commits the moves. The pass is non-destructive: Sortio keeps backup archives
            of your most recent sorts, so the whole backlog cleanup can be undone from inside
            the app if a batch comes out wrong. For sensitive drives, run the sort with local-only inference
            through Ollama so file content never leaves the machine. The tradeoff between local
            and managed inference is laid out in the{' '}
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="text-sortio-primary hover:underline"
            >
              local AI vs cloud AI piece
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Dealing with the miscellaneous pile and the duplicates
          </h2>

          <p>
            Two things make shared-drive cleanups feel hopeless: the "Miscellaneous" folder and
            the duplicate sprawl. Both are content problems disguised as filing problems.
          </p>

          <p>
            The miscellaneous pile exists because the filenames are useless, so nobody could
            classify the files and they got dumped instead. Reading the content dissolves most
            of it. Point the same prompt at the miscellaneous folder on its own, and the files
            that were unclassifiable by name become obvious by content: a{' '}
            <code>scan_0421.pdf</code> is a signed lease, a <code>document(7).docx</code> is a
            statement of work for a specific client. The genuine residue, the few files that
            really do not fit, lands in <code>_Holding/</code>, which is a labelled, finite
            queue rather than a black hole.
          </p>

          <p>
            Duplicates surface naturally in the preview. When the tool proposes the same
            destination name for two files, you see the collision in one view and decide which
            copy is canonical, keeping the most recent or most complete version, before
            anything moves. Going forward, the naming template itself suppresses new duplicates:
            identical content from the same source resolves to an identical, searchable name, so
            people stop creating <code>final_v3.pdf</code> because they can find the original.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Manual task force vs rule-based vs AI-by-content
          </h2>

          <p>
            There are three honest ways to do a shared-drive cleanup. They are not strictly
            better or worse; they fit different drive sizes and different levels of variability.
            Here is the fair comparison.
          </p>

          <div className="overflow-x-auto not-prose my-6">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-apple-700/50">
                  <th className="py-3 pr-4 text-apple-50 font-semibold">Approach</th>
                  <th className="py-3 px-4 text-apple-50 font-semibold">How the backlog gets done</th>
                  <th className="py-3 px-4 text-apple-50 font-semibold">Best for</th>
                  <th className="py-3 pl-4 text-apple-50 font-semibold">Breaks down when</th>
                </tr>
              </thead>
              <tbody className="text-apple-300">
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">Manual task force</td>
                  <td className="py-3 px-4 align-top">People open each file and drag it to a folder, renaming by hand.</td>
                  <td className="py-3 px-4 align-top">A small drive, a few hundred files, or a one-off tidy.</td>
                  <td className="py-3 pl-4 align-top">Thousands of files. It is dreaded, deprioritized, and rarely finished.</td>
                </tr>
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">Rule-based (Hazel, File Juggler)</td>
                  <td className="py-3 px-4 align-top">Filename or text-layer patterns map to folders via rules you author.</td>
                  <td className="py-3 px-4 align-top">Predictable sources where the same format and naming arrive every time.</td>
                  <td className="py-3 pl-4 align-top">Legacy files have inconsistent names, so the patterns do not match the backlog.</td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">AI-by-content (Sortio)</td>
                  <td className="py-3 px-4 align-top">An LLM reads each file and proposes a name and destination from its meaning, in a preview.</td>
                  <td className="py-3 px-4 align-top">Large, mixed, inconsistently named backlogs and scanned intake.</td>
                  <td className="py-3 pl-4 align-top">Cost and speed: each AI sort uses your AI allowance and is slower than a regex rule.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            Hazel and File Juggler are genuinely good tools. Hazel is the long-standing macOS
            automation app, rule-based with regex matching on the text layer; File Juggler is
            its Windows-only counterpart. The catch for a cleanup specifically is that the
            backlog is exactly where filenames are inconsistent, so the patterns you would write
            do not match the legacy mess. Rule-based tools shine on the going-forward intake,
            where the format is predictable, which is why most teams end up using AI-by-content
            for the messy backlog and a deterministic rule for the clean intake stream. The full
            breakdown of when each applies is in the{' '}
            <Link
              to="/blog/ai-sort-vs-rule-builder-which-to-use"
              className="text-sortio-primary hover:underline"
            >
              AI Sort vs Rule Builder
            </Link>{' '}
            piece.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Step 3: keep it clean with a standing rule
          </h2>

          <p>
            A clean drive that has no intake rule is a drive that will be messy again in six
            months. The going-forward fix is to move enforcement to the point of filing. On
            Sortio Pro and Teams you point a watch folder at the <code>_Inbox/</code> in your
            structure, and the same template that cleaned the backlog now applies to every new
            file automatically, with no one opening the app.
          </p>

          <ol className="list-decimal pl-6 space-y-2">
            <li>
              Tell the team there is one place to drop new files: the <code>_Inbox/</code>
              folder. They do not file anything by hand anymore.
            </li>
            <li>
              Add that folder as a watch folder in Sortio and paste the cleanup prompt as the
              rule.
            </li>
            <li>
              For the first week, leave the watch in Preview mode. Sortio queues proposed moves
              and notifies you instead of applying them, so you can confirm it is routing
              correctly on real intake.
            </li>
            <li>
              After a week of clean previews, switch to Apply. From then on, every file dropped
              in the inbox is named and routed within seconds.
            </li>
          </ol>

          <p>
            On Teams the rule lives in an admin console and is shared across every seat, so the
            convention is identical on every machine instead of each person interpreting it.
            That is the difference between a drive that holds and one that drifts. The naming
            side of enforcement is covered in depth in the{' '}
            <Link
              to="/blog/enforce-file-naming-convention"
              className="text-sortio-primary hover:underline"
            >
              enforce-a-naming-convention
            </Link>{' '}
            piece, and the time this gives back to staff is quantified in{' '}
            <Link
              to="/blog/reduce-time-filing-renaming-documents"
              className="text-sortio-primary hover:underline"
            >
              reduce the time staff spend filing and renaming
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A realistic cleanup timeline
          </h2>

          <div className="space-y-4 not-prose my-6">
            <div className="bg-apple-700/20 rounded-xl p-5 flex items-start gap-3">
              <Layers className="text-sortio-primary flex-shrink-0 mt-1" size={18} />
              <div>
                <h3 className="font-medium text-apple-50 mb-1">Day 1: decide the structure</h3>
                <p className="text-apple-300 text-sm">
                  Agree the folder tree and filename template with whoever owns the drive. Write
                  it down. This is a meeting, not a project.
                </p>
              </div>
            </div>
            <div className="bg-apple-700/20 rounded-xl p-5 flex items-start gap-3">
              <FolderTree className="text-sortio-primary flex-shrink-0 mt-1" size={18} />
              <div>
                <h3 className="font-medium text-apple-50 mb-1">Days 2 to 4: backlog pass in preview</h3>
                <p className="text-apple-300 text-sm">
                  Run the cleanup prompt one top-level folder at a time, review previews, resolve
                  the miscellaneous pile and duplicates, then apply. Backups cover any mistakes.
                </p>
              </div>
            </div>
            <div className="bg-apple-700/20 rounded-xl p-5 flex items-start gap-3">
              <Inbox className="text-sortio-primary flex-shrink-0 mt-1" size={18} />
              <div>
                <h3 className="font-medium text-apple-50 mb-1">Day 5 onward: standing rule on intake</h3>
                <p className="text-apple-300 text-sm">
                  Point a watch folder at the inbox in Preview for a week, confirm routing, then
                  switch to Apply. The drive now stays clean on its own.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/enforce-file-naming-convention"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Enforce a file naming convention your team actually follows
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/folder-structure-client-projects"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                A folder structure that holds up for client and project work
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/reduce-time-filing-renaming-documents"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Reduce the time staff spend filing and renaming documents
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Run the backlog pass on your drive</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance, no card required. Point Sortio at one
              messy folder, run Preview, and see the proposed names and destinations before
              anything moves. When you are ready to keep it clean across the team, Sortio for
              Teams adds shared rules and an admin console at $29 per seat per month.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/download"
                className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
              >
                Download Sortio
                <ArrowRight className="ml-2" size={16} />
              </Link>
              <Link
                to="/business"
                className="inline-flex items-center px-6 py-3 bg-apple-700/40 hover:bg-apple-700/60 text-apple-100 font-medium rounded-xl transition-colors duration-200"
              >
                See Sortio for Teams
                <ArrowRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CleanUpMessySharedDriveBlogPage;
